import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Calendar,
    Server,
    HardDrive,
    BarChart3,
    Bell,
    FileBarChart2,
    History,
    LogOut,
    BookOpen,
    MoonStar,
    Sun,
    ChevronDown,
    Camera,
    Package,
    Settings as SettingsIcon,
    Crown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { dragRegionStyle, isDesktop, useWindowControlsOverlayInset } from "@/lib/desktopShell";
import DesktopNotificationBridge from "@/components/DesktopNotificationBridge";
import DesktopActivationBanner from "@/components/DesktopActivationBanner";
import { AlertBell, AlertBanner } from "@/context/AlertsContext";
import PlanLimitBanner from "@/components/PlanLimitBanner";
import SimulationBadge from "@/components/SimulationBadge";
import SystemBanner from "@/components/SystemBanner";
import OnboardingWizard from "@/components/OnboardingWizard";
import ContextualHelpButton from "@/components/ContextualHelpButton";

// Sidebar reflects the actual shipped features. Dashboard sits ungrouped at
// the very top (a single-item "Control" group added nothing but a redundant
// label). Groups below follow the user's mental model: what to move
// (Migrations), what's doing the moving (Infrastructure — Download Agent
// leads here since it's the prerequisite for everything else in the group),
// what happened (Insights). Settings lives at the bottom as a collapsible
// dropdown so the routine pages stay uncluttered while the admin surfaces
// remain one click away.
const NAV_GROUPS = [
    {
        title: null,
        items: [
            { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-overview", exact: true },
        ],
    },
    {
        title: "Migrations",
        items: [
            { to: "/migrations", label: "PC Migration", icon: Camera, testId: "nav-migrations" },
            { to: "/migrate/server", label: "Server Migration", icon: Server, testId: "nav-migrate-server" },
            { to: "/migrate/storage", label: "Data Migration", icon: HardDrive, testId: "nav-migrate-storage" },
        ],
    },
    {
        title: "Infrastructure",
        items: [
            // "Download Agent" removed from here (was /access-tokens?tab=deploy-agent) —
            // that flow no longer has a sidebar entry point anywhere in the app.
            { to: "/agents", label: "Devices", icon: Server, testId: "nav-agents" },
            { to: "/schedules", label: "Backups", icon: Calendar, testId: "nav-schedules" },
        ],
    },
    {
        title: "Insights",
        items: [
            { to: "/analytics", label: "Analytics", icon: BarChart3, testId: "nav-analytics" },
            { to: "/alerts", label: "Alerts", icon: Bell, testId: "nav-alerts" },
            { to: "/reports", label: "Reports", icon: FileBarChart2, testId: "nav-reports" },
            { to: "/audit", label: "Audit Log", icon: History, testId: "nav-audit", minRole: "auditor" },
        ],
    },
];

// Settings is a dropdown: clicking the parent toggles the child list.
// It always sits at the bottom of the sidebar regardless of how many
// items get added — admin surfaces shouldn't compete with the daily
// workflow for vertical real-estate.
// Team & Roles, Notifications, Retention, SSO & SCIM, Security (2FA), API
// Tokens, and Desktop App were removed from here — Team & Roles duplicated
// the Portal's own Team tab, Desktop App has no sidebar entry point left,
// and the other four now live as tabs under the client Portal's Settings
// page (frontend/src/pages/portal/PortalSettings.jsx) instead of here.
const SETTINGS_GROUP = {
    title: "Settings",
    items: [
        { to: "/app-mappings", label: "App Mappings", icon: Package, testId: "nav-app-mappings", minRole: "admin" },
    ],
};

// Maps the current pathname to a page title shown in the header bar. Falls
// back to "Overview" if no match. /access-tokens hosts two tabs (Tokens vs
// Download Agent) behind one pathname — handled as a special case in
// computePageTitle below rather than here, since this table only sees path.
const PAGE_TITLES = [
    [/^\/$/, "Dashboard"],
    [/^\/dashboard/, "Dashboard"],
    [/^\/migrate\/server/, "Server Migration"],
    [/^\/migrate\/storage/, "Data Migration"],
    [/^\/migrations/, "PC Migration"],
    [/^\/migrate/, "New Migration"],
    [/^\/jobs\/[^/]+/, "Job Detail"],
    [/^\/jobs/, "Jobs"],
    [/^\/schedules/, "Backups"],
    [/^\/analytics/, "Analytics"],
    [/^\/alerts/, "Alerts"],
    [/^\/reports/, "Reports"],
    [/^\/audit/, "Audit Log"],
    [/^\/agents/, "Devices"],
    [/^\/notifications/, "Notifications"],
    [/^\/users/, "Team & Roles"],
    [/^\/retention/, "Retention"],
    [/^\/access-tokens/, "API Tokens"],
    [/^\/api-tokens/, "API Tokens"],
    [/^\/sso-settings/, "SSO & SCIM"],
    [/^\/security/, "Security"],
    [/^\/desktop-settings/, "Desktop Settings"],
    [/^\/settings/, "Settings"],
    [/^\/docs/, "User Guide"],
];

function computePageTitle(pathname, search) {
    if (pathname.startsWith("/access-tokens") && new URLSearchParams(search).get("tab") === "deploy-agent") {
        return "Download Agent";
    }
    return (PAGE_TITLES.find(([re]) => re.test(pathname)) || [, "Dashboard"])[1];
}

const ROLE_RANK = { viewer: 1, auditor: 2, engineer: 3, admin: 4, superadmin: 5 };

/** Split a nav item's `to` into its base path and an optional `?tab=` value —
 * the only query param any current nav item disambiguates on. */
function parseNavTo(to) {
    const [base, query = ""] = (to || "/").split("?");
    return { base: base || "/", tab: new URLSearchParams(query).get("tab") };
}

/** Pick exactly one nav item to highlight for the given location. Items
 * earlier in NAV_GROUPS win when multiple share the same base path; among
 * those, the one whose `?tab=` (if any) matches the current tab wins —
 * needed because "Download Agent" and "API Tokens" both point at
 * /access-tokens, distinguished only by tab. */
function computeActiveTestId(pathname, search, allGroups, userRank) {
    const visible = [];
    for (const g of allGroups) {
        for (const item of g.items) {
            if (item.minRole && userRank < (ROLE_RANK[item.minRole] || 0)) continue;
            visible.push(item);
        }
    }
    const currentTab = new URLSearchParams(search).get("tab") || null;

    const sameBase = visible.filter((i) => parseNavTo(i.to).base === pathname);
    if (sameBase.length > 0) {
        const tabMatch = sameBase.find((i) => parseNavTo(i.to).tab === currentTab);
        if (tabMatch) return tabMatch.testId;
        const noTabRequirement = sameBase.find((i) => !parseNavTo(i.to).tab);
        if (noTabRequirement) return noTabRequirement.testId;
        return sameBase[0].testId;
    }

    let best = null;
    let bestLen = -1;
    for (const i of visible) {
        const base = parseNavTo(i.to).base;
        if (base === "/") continue;
        if (pathname === base || pathname.startsWith(base + "/")) {
            if (base.length > bestLen) {
                best = i;
                bestLen = base.length;
            }
        }
    }
    if (best) return best.testId;
    const root = visible.find((i) => parseNavTo(i.to).base === "/");
    return root ? root.testId : null;
}

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const navigate = useNavigate();
    const { pathname, search } = useLocation();

    const userRank = ROLE_RANK[user?.role] || 0;
    const activeTestId = computeActiveTestId(pathname, search, [...NAV_GROUPS, SETTINGS_GROUP], userRank);
    const pageTitle = computePageTitle(pathname, search);

    // Settings dropdown: collapsed by default, but auto-expanded whenever
    // the user is sitting on one of the routes inside it. This avoids the
    // surprising "I'm on a Settings page but the sidebar shows no
    // breadcrumb" UX after a hard refresh.
    const settingsPathSet = useMemo(
        () => new Set(SETTINGS_GROUP.items.map((i) => i.to)),
        [],
    );
    const onSettingsRoute = useMemo(() => {
        for (const p of settingsPathSet) {
            if (pathname === p || pathname.startsWith(p + "/")) return true;
        }
        return false;
    }, [pathname, settingsPathSet]);

    const [settingsOpen, setSettingsOpen] = useState(onSettingsRoute);
    useEffect(() => {
        if (onSettingsRoute) setSettingsOpen(true);
    }, [onSettingsRoute]);

    // Space to reserve on the right of the header so nothing (the user
    // menu, in practice) renders underneath Windows' native title-bar
    // overlay buttons -- see useWindowControlsOverlayInset's own comment.
    // 0 in the plain browser and on first paint before the overlay
    // geometry is known.
    const overlayInset = useWindowControlsOverlayInset();

    // User dropdown (logout lives in here now).
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);
    useEffect(() => {
        if (!userMenuOpen) return;
        const onDown = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
                setUserMenuOpen(false);
            }
        };
        const onEsc = (e) => { if (e.key === "Escape") setUserMenuOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onEsc);
        };
    }, [userMenuOpen]);

    const doLogout = async () => {
        setUserMenuOpen(false);
        await logout();
        navigate("/login", { replace: true });
    };

    const initials = (user?.name || user?.email || "U")
        .split(/[\s@.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase())
        .join("") || "U";

    const visibleSettingsItems = SETTINGS_GROUP.items.filter(
        (i) => (!i.minRole || userRank >= (ROLE_RANK[i.minRole] || 0)) && (!i.desktopOnly || isDesktop()),
    );
    const settingsHasActive = visibleSettingsItems.some((i) => activeTestId === i.testId);

    return (
        <div className="flex min-h-screen bg-background">
            {isDesktop() && <DesktopNotificationBridge />}
            {/* SIDEBAR */}
            <aside
                className="mb-sidebar sticky top-0 flex h-screen w-[244px] shrink-0 flex-col"
                data-testid="app-sidebar"
            >
                <div className="flex items-center px-4 pb-3 pt-5" style={dragRegionStyle}>
                    <img
                        src="/brand/moovbytes-logo.png"
                        alt="MoovBytes"
                        className="h-8 w-auto"
                        style={{ filter: "brightness(0) invert(1)" }}
                        data-testid="app-logo"
                    />
                </div>

                <nav className="mb-sidebar-scroll flex-1 overflow-y-auto px-3 pb-3 pt-2">
                    {NAV_GROUPS.map((group, gi) => (
                        <div key={gi} className="mt-[14px] first:mt-1">
                            {group.title && (
                                <div
                                    className="px-3 pb-1 pt-1 text-[11px] font-normal uppercase text-white"
                                    style={{ letterSpacing: "1.2px", opacity: 0.6 }}
                                >
                                    {group.title}
                                </div>
                            )}
                            <ul className="space-y-[1px]">
                                {group.items.map((item) => {
                                    if (item.minRole && userRank < (ROLE_RANK[item.minRole] || 0)) return null;
                                    const Icon = item.icon;
                                    const isActive = activeTestId === item.testId;
                                    return (
                                        <li key={item.testId}>
                                            <Link
                                                to={item.to}
                                                data-testid={item.testId}
                                                className={[
                                                    "group relative flex items-center gap-3 rounded-md pl-3 pr-2.5 py-[7px] text-[13px] transition-all",
                                                    isActive
                                                        ? "bg-gradient-to-r from-[#004AAD] to-[#00C2FF] text-white shadow-[0_0_24px_-6px_rgba(0,194,255,0.55)] font-semibold"
                                                        : "text-white/70 hover:bg-white/[0.06] hover:text-white font-medium",
                                                ].join(" ")}
                                            >
                                                {isActive && (
                                                    <span className="pointer-events-none absolute -left-[3px] top-1.5 bottom-1.5 w-[3px] rounded-full bg-[#00C2FF] shadow-[0_0_8px_rgba(0,194,255,0.8)]" />
                                                )}
                                                <Icon size={18} strokeWidth={isActive ? 2.3 : 2}
                                                      className={isActive ? "opacity-100" : "opacity-85"} />
                                                <span className="flex-1">{item.label}</span>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </nav>

                {/* Settings dropdown — pinned at the bottom. The button
                    toggles the items list; we auto-open it when the user
                    is on a Settings route so they never lose their place
                    after a refresh. */}
                {visibleSettingsItems.length > 0 && (
                    <div className="mx-3 border-t border-white/10 py-2" data-testid="sidebar-settings-group">
                        <button
                            type="button"
                            onClick={() => setSettingsOpen((v) => !v)}
                            data-testid="sidebar-settings-toggle"
                            aria-expanded={settingsOpen}
                            className={[
                                "group flex w-full items-center gap-3 rounded-md pl-3 pr-2.5 py-[7px] text-[13px] transition-all",
                                settingsHasActive
                                    ? "text-white font-semibold"
                                    : "text-white/75 hover:bg-white/[0.06] hover:text-white font-medium",
                            ].join(" ")}
                        >
                            <SettingsIcon size={18} strokeWidth={settingsHasActive ? 2.3 : 2} />
                            <span className="flex-1 text-left">Settings</span>
                            <ChevronDown
                                size={14}
                                className={`transition-transform ${settingsOpen ? "rotate-180" : ""}`}
                            />
                        </button>
                        {settingsOpen && (
                            <ul className="mt-1 space-y-[1px] pl-2" data-testid="sidebar-settings-items">
                                {visibleSettingsItems.map((item) => {
                                    const Icon = item.icon;
                                    const isActive = activeTestId === item.testId;
                                    return (
                                        <li key={item.testId}>
                                            <Link
                                                to={item.to}
                                                data-testid={item.testId}
                                                className={[
                                                    "group relative flex items-center gap-3 rounded-md pl-3 pr-2.5 py-[6px] text-[12.5px] transition-all",
                                                    isActive
                                                        ? "bg-gradient-to-r from-[#004AAD] to-[#00C2FF] text-white shadow-[0_0_18px_-6px_rgba(0,194,255,0.55)] font-semibold"
                                                        : "text-white/65 hover:bg-white/[0.05] hover:text-white",
                                                ].join(" ")}
                                            >
                                                {isActive && (
                                                    <span className="pointer-events-none absolute -left-[3px] top-1.5 bottom-1.5 w-[3px] rounded-full bg-[#00C2FF] shadow-[0_0_8px_rgba(0,194,255,0.8)]" />
                                                )}
                                                <Icon size={15} strokeWidth={isActive ? 2.3 : 2}
                                                      className={isActive ? "opacity-100" : "opacity-75"} />
                                                <span className="flex-1">{item.label}</span>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                )}

                {/* Footer — collapse hint removed per UI refresh; sidebar is
                    now always-visible. */}
            </aside>

            <div className="flex min-w-0 flex-1 flex-col">
                <header
                    className="flex h-16 items-center justify-between border-b border-border bg-surface px-6"
                    style={overlayInset ? { paddingRight: `calc(1.5rem + ${overlayInset}px)` } : undefined}
                >
                    <div className="flex items-center gap-4">
                        <div className="font-display text-xl font-semibold tracking-tight" data-testid="header-title">
                            {pageTitle}
                        </div>
                        <SimulationBadge />
                    </div>
                    <div className="flex items-center gap-3">
                        <AlertBell />
                        <Link
                            to="/migrate"
                            data-testid="header-new-job-btn"
                            className="mb-focus rounded-md bg-gradient-to-r from-[#5b5ef0] to-[#7c5cff] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white shadow-lg shadow-[#5b5ef0]/25 transition-all hover:shadow-xl hover:shadow-[#5b5ef0]/40"
                        >
                            + New Migration
                        </Link>
                        <div className="flex items-center gap-2.5 border-l border-border pl-3" data-testid="header-user">
                            <button
                                onClick={toggle}
                                data-testid="theme-toggle"
                                aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
                                title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
                                className="mb-focus inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                                {theme === "dark" ? <Sun size={15} /> : <MoonStar size={15} />}
                            </button>

                            {/* User avatar + dropdown. Clicking opens a menu
                                that hosts the Log out action (moved from
                                the sidebar). Click-outside + Esc dismiss. */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    type="button"
                                    onClick={() => setUserMenuOpen((v) => !v)}
                                    aria-haspopup="menu"
                                    aria-expanded={userMenuOpen}
                                    data-testid="header-user-btn"
                                    className="mb-focus flex items-center gap-2 rounded-full p-0.5 pr-2 transition-colors hover:bg-muted"
                                >
                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-sm font-bold text-background">
                                        {initials}
                                    </div>
                                    <div className="hidden leading-tight md:block text-left">
                                        <div className="text-sm font-semibold">{user?.name || "—"}</div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                                            {user?.role}
                                        </div>
                                    </div>
                                    <ChevronDown size={13} className="hidden md:block text-muted-foreground" />
                                </button>

                                {userMenuOpen && (
                                    <div
                                        role="menu"
                                        data-testid="header-user-menu"
                                        className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-lg border border-border bg-surface shadow-2xl"
                                    >
                                        <div className="border-b border-border px-3 py-3">
                                            <div className="truncate text-sm font-semibold">{user?.name || "—"}</div>
                                            <div className="truncate text-[11px] text-muted-foreground">{user?.email}</div>
                                            <div className="mt-1 inline-flex items-center rounded-full border border-border bg-background/50 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                                                {user?.role}
                                            </div>
                                        </div>
                                        <Link
                                            to="/docs"
                                            onClick={() => setUserMenuOpen(false)}
                                            role="menuitem"
                                            data-testid="header-docs-link"
                                            className="flex w-full items-center gap-2 border-b border-border px-3 py-2.5 text-left text-[13px] text-foreground transition-colors hover:bg-muted/40"
                                        >
                                            <BookOpen size={14} /> User guide
                                        </Link>
                                        {user?.role === "superadmin" && (
                                            <Link
                                                to="/superadmin"
                                                onClick={() => setUserMenuOpen(false)}
                                                role="menuitem"
                                                data-testid="header-superadmin-link"
                                                className="flex w-full items-center gap-2 border-b border-border px-3 py-2.5 text-left text-[13px] text-foreground transition-colors hover:bg-muted/40"
                                            >
                                                <Crown size={14} /> Super Admin
                                            </Link>
                                        )}
                                        <button
                                            type="button"
                                            onClick={doLogout}
                                            role="menuitem"
                                            data-testid="header-logout-btn"
                                            className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] text-[hsl(var(--destructive))] transition-colors hover:bg-[hsl(var(--destructive))]/10"
                                        >
                                            <LogOut size={14} /> Log out
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>
                <SystemBanner />
                <AlertBanner />
                {isDesktop() && <DesktopActivationBanner />}
                <main className="flex-1 overflow-y-auto">
                    <PlanLimitBanner />
                    {children}
                </main>
                <footer className="flex items-center justify-between border-t border-border bg-surface px-6 py-3 text-[11px] text-muted-foreground">
                    <div>© {new Date().getFullYear()} MoovBytes. All rights reserved.</div>
                    <div className="font-mono uppercase tracking-[0.18em]">v1.0.0</div>
                </footer>
            </div>
            {/* First-run onboarding wizard. Self-fetches its state and only
                opens once per user (or on demand from the user menu later). */}
            <OnboardingWizard />
            {/* Floating help button — deep-links into the User Guide at
                the section relevant to the current route. */}
            <ContextualHelpButton />
        </div>
    );
}
